import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/auth';
import { TokenService } from '../services/auth/TokenService';
import { LiffTokenService } from '../services/auth/LiffTokenService';
import { TeacherIdManager } from '../utils/teacherIdManager';
import { RedirectPathService } from '../services/auth/RedirectPathService';

const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
  statusCode: null
};

const AUTH_ACTIONS = {
  AUTH_REQUEST: 'AUTH_REQUEST',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_FAILURE: 'AUTH_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.AUTH_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };

    case AUTH_ACTIONS.AUTH_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        loading: false,
        error: null
      };

    case AUTH_ACTIONS.AUTH_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload.error,
        statusCode: action.payload.statusCode
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
        statusCode: null
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload.updates }
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        statusCode: null
      };


    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        TokenService.cleanupInvalidTokens();
        
        const accessToken = TokenService.getAccessToken();
        if (accessToken && TokenService.isValidToken(accessToken)) {
          const user = TokenService.getUserFromToken(accessToken);
          const persistedTeacherId = TokenService.getTeacherId();
          
          if (user && (user.teacherId || user.id)) {
            if (!persistedTeacherId && user.teacherId) {
              TeacherIdManager.ensurePersisted(user.teacherId);
            }
            
            dispatch({
              type: AUTH_ACTIONS.AUTH_SUCCESS,
              payload: { user }
            });
            
            try {
              const serverUser = await authService.verifyToken();
              if (serverUser) {
                dispatch({
                  type: AUTH_ACTIONS.UPDATE_USER,
                  payload: { updates: serverUser }
                });
              }
            } catch (error) {
            }
          } else {
            TokenService.removeTokens();
            TeacherIdManager.clear();
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          }
        } else {
          TokenService.removeTokens();
          TeacherIdManager.clear();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch (error) {
        TokenService.removeTokens();
        TeacherIdManager.clear();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    // TODO: Uncomment when backend refresh token API is implemented
    /*
    const attemptTokenRefresh = async () => {
      const refreshToken = TokenService.getRefreshToken();
      if (refreshToken && TokenService.isValidToken(refreshToken)) {
        try {
          const refreshResult = await authService.refreshToken();
          if (refreshResult && refreshResult.user) {
            dispatch({
              type: AUTH_ACTIONS.AUTH_SUCCESS,
              payload: { user: refreshResult.user }
            });
          } else {
            throw new Error('Invalid refresh response');
          }
        } catch (refreshError) {
          TokenService.removeTokens();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        TokenService.removeTokens();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };
    */


    initializeAuth();
  }, []);

  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
    
    dispatch({ type: AUTH_ACTIONS.AUTH_REQUEST });
    
    try {
      if (!credentials.username || !credentials.password) {
        throw new Error('Username and password are required');
      }
      
      if (credentials.username.trim().length < 3) {
        throw new Error('Username must be at least 3 characters long');
      }
      
      if (credentials.password.length < 4) {
        throw new Error('Password must be at least 4 characters long');
      }

      const response = await authService.login({
        username: credentials.username.trim(),
        password: credentials.password,
        ...(credentials.idToken && { idToken: credentials.idToken })
      });
      
      const { user, accessToken, refreshToken } = response;

      // Validate user role - only teachers are allowed
      if (!user || user.role !== 'teacher') {
        const roleError = new Error('Access denied: Only teachers are allowed to use this application');
        roleError.response = { status: 403, data: { message: 'Access denied: Only teachers are allowed' } };
        throw roleError;
      }

      const loginMethod = (credentials.idToken || LiffTokenService.hasIdToken()) ? 'liff' : 'password';
      
      TokenService.setTokens(accessToken, refreshToken, user?.teacherId, loginMethod);

      dispatch({
        type: AUTH_ACTIONS.AUTH_SUCCESS,
        payload: { user }
      });

      return { success: true, user };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      const statusCode = error.response?.status;
      
      dispatch({
        type: AUTH_ACTIONS.AUTH_FAILURE,
        payload: { 
          error: errorMessage,
          statusCode: statusCode
        }
      });

      throw { 
        message: errorMessage, 
        statusCode: statusCode,
        originalError: error 
      };
    }
  };

  const logout = async () => {
    try {
      if (TokenService.isLiffLogin()) {
        await authService.logoutLiff();
      } 
    } catch (error) {
    } finally {
      TokenService.removeTokens();
      TeacherIdManager.clear();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const { user, accessToken, refreshToken } = response;

      // Store tokens and teacherId (registration defaults to password login)
      TokenService.setTokens(accessToken, refreshToken, user?.teacherId, 'password');

      dispatch({
        type: AUTH_ACTIONS.AUTH_SUCCESS,
        payload: { user }
      });

      return { success: true, user };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      
      dispatch({
        type: AUTH_ACTIONS.AUTH_FAILURE,
        payload: { error: errorMessage }
      });

      throw new Error(errorMessage);
    }
  };

  const updateUser = async (updates) => {
    try {
      const updatedUser = await authService.updateProfile(updates);
      
      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: { updates: updatedUser }
      });

      return updatedUser;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Update failed';
      throw new Error(errorMessage);
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const setRedirectPath = (path) => {
    if (!path) {
      return;
    }

    
    const success = RedirectPathService.setRedirectPath(path);
    
    if (success) {
      const normalizedPath = RedirectPathService.normalizePath(path);
      dispatch({
        type: AUTH_ACTIONS.SET_REDIRECT_PATH,
        payload: { path: normalizedPath }
      });
    }
  };

  const clearRedirectPath = () => {
    RedirectPathService.clearRedirectPath();
    dispatch({ type: AUTH_ACTIONS.CLEAR_REDIRECT_PATH });
  };

  const getRedirectPath = (fallbackPath = '/calendar') => {
    const localStoragePath = RedirectPathService.getRedirectPath(null);
    const path = localStoragePath || state.redirectPath || fallbackPath;
    return path;
  };


  const reinitializeAuth = async () => {
    dispatch({ type: AUTH_ACTIONS.AUTH_REQUEST });
    
    try {
      TokenService.cleanupInvalidTokens();
      
      const accessToken = TokenService.getAccessToken();
      if (accessToken && TokenService.isValidToken(accessToken)) {
        const user = TokenService.getUserFromToken(accessToken);
        const persistedTeacherId = TokenService.getTeacherId();
        
        if (user && (user.teacherId || user.id)) {
          if (!persistedTeacherId && user.teacherId) {
            TeacherIdManager.ensurePersisted(user.teacherId);
          }
          
          dispatch({
            type: AUTH_ACTIONS.AUTH_SUCCESS,
            payload: { user }
          });
          
          return { success: true, user };
        } else {
          TokenService.removeTokens();
          TeacherIdManager.clear();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
          return { success: false, error: 'Invalid user data' };
        }
      } else {
        TokenService.removeTokens();
        TeacherIdManager.clear();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        return { success: false, error: 'No valid tokens' };
      }
    } catch (error) {
      TokenService.removeTokens();
      TeacherIdManager.clear();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      return { success: false, error: error.message };
    }
  };

  const getTeacherId = () => {
    // Use TeacherIdManager for consistent teacher ID access
    return state.user?.teacherId || TeacherIdManager.getCurrentTeacherId();
  };

  const getPrimaryId = () => {
    return getTeacherId();
  };

  const contextValue = {
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    loading: state.loading,
    error: state.error,
    statusCode: state.statusCode,
    
    login,
    logout,
    register,
    updateUser,
    clearError,
    reinitializeAuth,
    setRedirectPath,
    clearRedirectPath,
    getRedirectPath,
    
    getPrimaryId,
    getTeacherId
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;